<template>
  <b-container>
    <b-row>
      <b-col>
        <div class="form form-sm my-4 my-lg-5 mx-auto">
          <FormHeader v-if="!showForm2FA && !showSetPassword" bookmarkable> Welcome to LimeIQ! </FormHeader>

          <Form2FA
            v-if="showForm2FA"
            :email="formData.email"
            :password="formData.password"
            :type="otpType"
            :deviceEnabled="deviceEnabled"
          />
          <template v-else>
            <SsoSetPassword
              v-if="showSetPassword"
              :email="formData.email"
              :sso-login-url="ssoLoginUrl"
            />

            <template v-else>
            <b-alert
              v-if="showFirstTimePrompt"
              variant="success"
              show
              class="text-center mb-4"
            >
              <p class="mb-3">
                Due to the planned implementation of SSO capabilities and
                enhanced security protocols, all users are required to change
                their password. Please select <strong>NEXT</strong> to reset
                your password and login.
              </p>
              <base-button type="primary" size="lg" block @click="continueToSso">
                Next
              </base-button>
            </b-alert>

            <!-- The form is held back until /auth/config/ says whether this
                 environment still takes passwords, so that a page about to
                 redirect to SSO never flashes a password form first. Show that
                 the page is working rather than an empty card while we wait. -->
            <div
              v-if="!configLoaded || resolvingPrefill"
              class="d-flex align-items-center justify-content-center small py-4"
            >
              <b-spinner small class="mr-2" />
              Loading...
            </div>

            <validation-observer
              v-if="configLoaded && !resolvingPrefill && !showFirstTimePrompt && legacyLoginEnabled"
              v-slot="{ invalid }"
              ref="form"
              class="form form-sm mx-auto"
            >
              <b-form @submit.prevent="handleSubmit">
                <base-input
                  v-model="formData.email"
                  rules="required|email"
                  name="Email Address"
                  label="Email Address"
                ></base-input>

                <base-input
                  v-model="formData.password"
                  rules="required"
                  name="Password"
                  label="Password"
                  type="password"
                ></base-input>

                <TermlyCheckbox v-model="formData.terms" />

                <router-link
                  :to="{ name: 'ForgotPassword' }"
                  class="text-dark text-right d-block small font-weight-bold"
                  >Forgot Password?</router-link
                >

                <base-button
                  :disabled="invalid || checkingSso"
                  :loading="formProcessing"
                  type="primary"
                  native-type="submit"
                  size="lg"
                  class="mt-4"
                  block
                >
                  {{ checkingSso ? "Checking sign-in method..." : "Log In" }}
                </base-button>
              </b-form>
            </validation-observer>

            <!-- The magic-link endpoint is gated by the same environment flag as
                 password sign-in, so when legacy login is off this link leads to
                 a page whose only button returns 403. Hide it rather than offer
                 a dead end. (When legacy login is off this page redirects away
                 entirely, so this is belt-and-braces for the in-between paint.) -->
            <div
              v-if="configLoaded && !resolvingPrefill && !showFirstTimePrompt && legacyLoginEnabled"
              class="mt-4 font-weight-bold text-center small"
            >
              Log in using
              <router-link :to="{ name: 'Home' }" class="text-dark text-underline"
                >magic link</router-link
              >
            </div>
            </template>
          </template>
        </div>
      </b-col>
    </b-row>
  </b-container>
</template>

<script>
import {
  defineComponent,
  onMounted,
  reactive,
  ref,
  watch,
} from "@vue/composition-api";
import { useRoute, useRouter } from "vue2-helpers/vue-router";
import _ from "lodash";

import { AuthenticationService } from "@services";
import { useSsoLogin } from "@composables";
import FormHeader from "@components/FormHeader";
import Form2FA from "@components/Form2FA";
import SsoSetPassword from "@components/SsoSetPassword";
import TermlyCheckbox from "@components/Termly/TermlyCheckbox";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default defineComponent({
  name: "Login",
  title: "Login",
  components: {
    FormHeader,
    Form2FA,
    SsoSetPassword,
    TermlyCheckbox,
  },
  setup() {
    // Pre-fill the email if it arrives as a query param (e.g. from an SSO
    // invite link at /login?email=...), so the first-time-SSO prompt can fire
    // without the user retyping their address.
    const route = useRoute();
    const router = useRouter();
    const prefillEmail = (route.query.email || "").trim();

    // email must exist at creation time, not be added later - Vue 2's
    // reactivity can't track a property added to an already-reactive object
    // after the fact, which would silently break the SSO watch() below.
    const formData = reactive({ email: prefillEmail });

    const formProcessing = ref(false);
    const showForm2FA = ref(false);
    const deviceEnabled = ref(false);
    const otpType = ref("email");
    // Holds the legacy form back while a prefilled email (from an SSO invite
    // link) is being resolved, same reasoning as configLoaded: without this,
    // the form briefly renders using default state before resolution replaces
    // it, because resolution normally runs through the 500ms-debounced
    // checkSso used for live typing. Stays false (no-op) when there's no
    // prefillEmail, so bare /login is unaffected.
    const resolvingPrefill = ref(!!prefillEmail);

    // Sign-in routing is shared with the magic-link page - see useSsoLogin.
    const {
      checkingSso,
      showFirstTimePrompt,
      showSetPassword,
      ssoLoginUrl,
      legacyLoginEnabled,
      configLoaded,
      loadAuthConfig,
      resolveSsoForEmail,
      continueToSso,
    } = useSsoLogin();

    // As soon as a full email is typed, ask whether that account has SSO
    // enabled - if so, hand off to Entra instead of accepting a password.
    // Debounced so it only fires once typing settles.
    const checkSso = _.debounce(async (email) => {
      if (!EMAIL_RE.test(email || "")) return;
      await resolveSsoForEmail(email);
    }, 500);

    watch(
      () => formData.email,
      (newEmail) => checkSso(newEmail)
    );

    onMounted(async () => {
      await loadAuthConfig();

      // /login's own password form is no longer the default experience,
      // regardless of legacyLoginEnabled: with SSO covering virtually
      // everyone, defaulting to a bare password form here read as "legacy is
      // still the primary sign-in method." Hand off unconditionally to
      // MagiclinkLogin (route name "Home") instead - it resolves the typed
      // address to SSO on submit exactly like this page's own watch below,
      // and falls back to emailing a magic link for anyone not SSO-enabled
      // (still available whenever legacyLoginEnabled is true), so the one
      // remaining non-migrated account keeps a way in via magic link rather
      // than a typed password. The query is carried over so an invite
      // link's ?email= survives the hop. Not an early return: this
      // component is about to be unmounted by the navigation regardless, so
      // the rest of this hook running in the meantime is harmless - the
      // target page performs the same resolution itself once mounted.
      router.replace({ name: "Home", query: route.query }).catch(() => {});

      // A pre-filled email (from an SSO invite link) doesn't trigger the watch,
      // so resolve it directly here - deliberately NOT through the debounced
      // checkSso used for live typing, which exists to avoid spamming the
      // endpoint while someone is mid-keystroke. That doesn't apply to a
      // complete address arriving from a URL, and routing it through the same
      // 500ms delay let the legacy form render first and then get replaced,
      // a visible flash on every invite link. Awaited so resolvingPrefill
      // covers the real resolution, not just the artificial debounce delay.
      if (prefillEmail) await resolveSsoForEmail(prefillEmail);
      resolvingPrefill.value = false;
    });

    const handleSubmit = async () => {
      formProcessing.value = true;

      const res = await AuthenticationService.login(
        formData.email,
        formData.password
      ).catch((err) => {
        formProcessing.value = false;
        // 403 + sso_login_url is the server saying this credential cannot be used
        // and where to go instead - either the account is migrated, or this
        // environment has switched password login off. Previously .catch()
        // discarded it and the user saw nothing happen at all. Every other error
        // keeps its existing behaviour.
        const body = (err && err.response && err.response.data) || {};
        const payload = body.data || body;
        if (payload.sso_required && payload.sso_login_url) {
          window.location.href = payload.sso_login_url;
        }
        return null;
      });

      if (!res) return;

      const { device_2fa_enabled } = res.data.data;

      formProcessing.value = false;
      showForm2FA.value = true;
      deviceEnabled.value = device_2fa_enabled;
      otpType.value = device_2fa_enabled ? "phone" : "email";
    };

    return {
      formData,
      formProcessing,
      showForm2FA,
      deviceEnabled,
      otpType,
      resolvingPrefill,
      checkingSso,
      showFirstTimePrompt,
      showSetPassword,
      ssoLoginUrl,
      legacyLoginEnabled,
      configLoaded,
      continueToSso,
      handleSubmit,
    };
  },
});
</script>
